'use client';

import type { DefaultHttpResponseListHomeInstitutionEventResponse } from '__generated__/api/unauth';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';

import { AuthQueryOptions, UnauthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { Image, LoadingPageTemplate } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useWebviewTypeEntry } from '@/hooks/useWebviewTypeEntry';
import { useAuthStore } from '@/stores/AuthStore';

interface EventListProps {
  initialData?: DefaultHttpResponseListHomeInstitutionEventResponse;
}

export const EventList = ({ initialData }: EventListProps) => {
  const isLogin = useAuthStore((state) => state.isLogin);
  const { entry } = useWebviewTypeEntry();

  const { data: orgEventList, isLoading } = useQuery({
    ...(isLogin
      ? AuthQueryOptions['get:/wello/v2/auth/home/institution-event']()
      : UnauthQueryOptions['get:/wello/v2/allows/home/institution-event']({
          query: {
            entry,
          },
        })),
    placeholderData: initialData,
    select: ({ context }) => context?.contents ?? [],
    enabled: isLogin !== undefined,
  });

  const convertOrgEventList =
    orgEventList?.map((item) => ({
      ...item,
      href: DONATION_ROUTES.MY_ETC_EVENT_DETAIL.pathname({
        eventId: item.eventId,
      }),
    })) ?? [];

  const eventList = [
    ...convertOrgEventList,
    entry === 'WELLO'
      ? {
          href: DONATION_ROUTES.GALAXY_WATCH.pathname,
          title: '갤워치 8 기브 배너 이미지',
          thumbnailUrl:
            'https://cdn.welfarehello.com/static/donation-event-galaxy-watch-thumbnail-small.png',
          endedYN: true,
        }
      : null,
  ].filter((item): item is NonNullable<typeof item> => item !== null);

  const isLoadingDimShow = isLoading || isLogin === undefined;

  return (
    <ul className={cn('grid grid-cols-2 gap-12', 'mobile-tablet:grid-cols-1')}>
      {eventList.map(({ href, thumbnailUrl, title, endedYN }, index) => (
        <li
          key={index}
          className="relative aspect-[335/120] overflow-hidden rounded-xlarge"
        >
          <CustomLink
            href={href}
            aria-label={`${title} 상세페이지로 이동`}
            className="absolute left-0 top-0 size-full object-cover"
          >
            <Image
              fill
              sizes={`${BREAKPOINTS.MOBILE_TABLET} 100vw, 484px`}
              src={thumbnailUrl}
              alt={`${title} 이벤트 썸네일 이미지`}
              className="object-cover"
            />
          </CustomLink>

          {endedYN ? (
            <p className="absolute right-12 top-12 rounded-xsmall bg-Gray-200 px-6 pb-2 pt-4 font-600 text-Gray-black typo-caption1">
              마감
            </p>
          ) : null}
        </li>
      ))}

      {isLoadingDimShow ? <LoadingPageTemplate unmountedPlaceholder /> : null}
    </ul>
  );
};
